import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  Column,
  Columns,
  Heading,
  HiddenVisually,
  IconLocation,
  IconMail,
  IconPhone,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';

import { formatMobile } from 'src/modules/utils';
import { normalizeName } from 'src/utils';

import translations from './PersonalDetails.vocab';

import * as styles from './PersonalDetailsView.css';

interface Props {
  onEditClick: () => void;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  countryCallingCode: string;
  homeLocationShortDesc: string;
  verifiedIdentityName: string;
}

const PersonalDetailsView: React.FC<Props> = ({
  firstName,
  lastName,
  emailAddress,
  phoneNumber,
  countryCallingCode,
  homeLocationShortDesc,
  verifiedIdentityName,
  onEditClick,
}) => {
  const { t } = useTranslations(translations);
  const verifiedIdentityNameUpper = verifiedIdentityName
    ? normalizeName(verifiedIdentityName)
    : '';
  const nameUpper = normalizeName(`${firstName} ${lastName}`);
  const verifiedIdentityLabel =
    verifiedIdentityNameUpper === nameUpper
      ? t('Identity verified')
      : `${t('Identity verified as name')} ${verifiedIdentityName}`;

  return (
    <Box
      position="relative"
      padding="gutter"
      background="brand"
      borderRadius="xlarge"
      overflow="hidden"
      className={styles.profileShapes}
    >
      <Box
        position={
          'relative' /** important to ensure content overlays background shapes */
        }
      >
        <Stack space="medium">
          <Inline space="xsmall" alignY="center">
            <Heading level="3">
              {firstName} {lastName}{' '}
            </Heading>
            {verifiedIdentityName ? (
              <Columns space="xxsmall">
                <Column width="content">
                  <Text tone="secondary">
                    <SeekPassIconVerified darkMode />
                  </Text>
                </Column>
                <Column>
                  <Text>{verifiedIdentityLabel}</Text>
                </Column>
              </Columns>
            ) : null}
          </Inline>

          <Stack space="small">
            <Text icon={<IconLocation tone="secondary" />}>
              {homeLocationShortDesc}
            </Text>
            <Text
              icon={<IconPhone tone="secondary" />}
              data={{ 'hj-suppress': 'true' }}
            >
              {`${countryCallingCode} ${formatMobile(phoneNumber)}`}
            </Text>
            <Text icon={<IconMail tone="secondary" />}>{emailAddress}</Text>
          </Stack>

          <Inline space="none">
            <Button
              variant="ghost"
              tone="neutral"
              onClick={onEditClick}
              data={{
                testid: 'edit-personal-details',
              }}
            >
              <span aria-hidden>{t('Edit')}</span>
              <HiddenVisually>{t('editPersonalDetails')}</HiddenVisually>
            </Button>
          </Inline>
        </Stack>
      </Box>
    </Box>
  );
};

export default PersonalDetailsView;
